import React, { useState } from "react";
import styled from "styled-components";
import Topmenu from "../Buttered_Topmenu";
import lang from "../../../language/Eng_Aust.json";
import { menuData } from "./Buttered_Menu_list";
import BottomMenu from "../Buttered_BottomMenu";
import { TransitionContainer } from "../animation/TransitionContainer";

const categories = [
  lang.itemMenu.all,
  lang.itemMenu.drink,
  lang.itemMenu.bread,
  lang.itemMenu.cake,
];

const subcategories: { [key: string]: string[] } = {
  [lang.itemMenu.drink]: [
    lang.drinkMenu.coffee,
    lang.drinkMenu.noncoffee,
    lang.drinkMenu.sweettea,
    lang.drinkMenu.looseleaftea,
    lang.drinkMenu.ade,
    lang.drinkMenu.babycino,
  ],
  [lang.itemMenu.bread]: [
    lang.breadMenu.saltedbreadroll,
    lang.breadMenu.bagel
  ],
  [lang.itemMenu.cake]: [
  ],
};

const hasMenuImage = (item: { image?: string }) =>
  Boolean(item.image && !item.image.includes("buttered_noimage.webp"));

const MenuListPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(lang.itemMenu.all);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredMenu = menuData.filter((item) => {
    const matchesCategory = selectedCategory === lang.itemMenu.all || item.category === selectedCategory;
    const matchesSubCategory = !selectedSubCategory || item.subcategory === selectedSubCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSubCategory && matchesSearch;
  });

  if (selectedCategory === lang.itemMenu.all) {
    filteredMenu.sort((a, b) => Number(hasMenuImage(b)) - Number(hasMenuImage(a)));
  }

  return (
    <TransitionContainer>
      <PageContainer>
        <Topmenu />
        <ContentContainer>
          <CategorySection>
            <CategoryTabsWrapper>
              <CategoryTabs>
                {categories.map((category) => (
                  <CategoryTab
                    key={category}
                    active={selectedCategory === category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setSelectedSubCategory(null);
                    }}
                  >
                    {category}
                  </CategoryTab>
                ))}
              </CategoryTabs>
              {subcategories[selectedCategory] && (
                <CategoryTabs isSub>
                  {subcategories[selectedCategory].map((sub) => (
                    <CategoryTab
                      key={sub}
                      active={selectedSubCategory === sub}
                      onClick={() => setSelectedSubCategory((prev) => (prev === sub ? null : sub))}
                    >
                      {sub}
                    </CategoryTab>
                  ))}
                </CategoryTabs>
              )}
            </CategoryTabsWrapper>
            <SearchSection>
              <SearchInput
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchSection>
          </CategorySection>
          <MenuGrid>
            {filteredMenu.map((item) => {
              const hasImage = hasMenuImage(item);
              return (
                <MenuCard key={item.id} hasImage={hasImage}>
                  {item.isHot && <HotBadge>HOT</HotBadge>}
                  {item.isIce && (
                    <IceBadge $isHotVisible={item.isHot}>
                      ICE
                    </IceBadge>
                  )}
                  {hasImage ? (
                    <MenuImageContainer>
                      <MenuImage src={item.image} alt={item.name} />
                    </MenuImageContainer>
                  ) : (
                    <NoImageContainer>
                      <NoImageText>Buttered</NoImageText>
                    </NoImageContainer>
                  )}
                  <MenuDetails hasImage={hasImage}>
                    <MenuName $hasImage={hasImage}>{item.name}</MenuName>
                    {item.price && <MenuPrice>${item.price}</MenuPrice>}
                  </MenuDetails>
                </MenuCard>
              );
            })}
          </MenuGrid>
        </ContentContainer>
        <BottomMenu />
      </PageContainer>
    </TransitionContainer>
  );
};

export default MenuListPage;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  background: #faf8f5;
`;

const ContentContainer = styled.div`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 32px 80px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 600px) { padding: 20px 10px; }
`;

const CategorySection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
  width: 100%;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
    margin-bottom: 20px;
  }
`;

const CategoryTabsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: #fffdf9;
  border-radius: 28px;
  padding: 12px;
  gap: 10px;
  flex: 1;
  box-shadow: 0 10px 32px rgba(56, 46, 35, 0.06);
  border: 1px solid #eee7dd;
`;

const CategoryTabs = styled.div<{ isSub?: boolean }>`
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: ${(props) => (props.isSub ? "flex-start" : "center")};
  gap: 10px;
  @media (max-width: 600px) { gap: 5px; }
`;

const CategoryTab = styled.button<{ active: boolean }>`
  background: ${(props) => (props.active ? "#ffe66d" : "transparent")};
  border: 1px solid ${(props) => (props.active ? "#ffe66d" : "#eee7dd")};
  padding: 11px 17px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: ${(props) => (props.active ? "600" : "500")};
  color: #40372c;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: ${(props) => (props.active ? "#ffe66d" : "#fff5cd")};
    transform: translateY(-2px);
  }
  @media (max-width: 600px) { font-size: 14px; padding: 8px 10px; }
`;

const SearchSection = styled.div`
  width: 220px;
  @media (max-width: 600px) { width: 100%; }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 15px 20px;
  border: 1px solid #eee7dd;
  border-radius: 999px;
  outline: none;
  box-sizing: border-box;
  background: #fffdf9;
  box-shadow: 0 10px 32px rgba(56, 46, 35, 0.06);
  &:focus { border-color: #e1bf35; box-shadow: 0 0 0 4px rgba(255,230,109,.25); }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  width: 100%;
  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); gap: 15px; }
`;

const MenuCard = styled.div<{ hasImage: boolean }>`
  position: relative;
  background: white;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(56, 46, 35, 0.07);
  transition: all 0.4s ease;
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => (props.hasImage ? "flex-start" : "center")};
  min-height: ${(props) => (props.hasImage ? "auto" : "220px")};
  border: 1px solid #eee7dd;
  &:hover { transform: translateY(-5px); box-shadow: 0 16px 36px rgba(56, 46, 35, 0.11); }
`;

const NoImageContainer = styled.div`
  width: 100%;
  height: 150px;
  background: #fffcf0;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 600px) { height: 120px; }
`;

const NoImageText = styled.span`
  font-size: 20px;
  color: #e4df8b;
  font-weight: 800;
  opacity: 0.7;
  letter-spacing: 1px;
`;

const MenuImageContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  @media (max-width: 600px) { height: 120px; }
`;

const MenuImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MenuDetails = styled.div<{ hasImage: boolean }>`
  padding: ${(props) => (props.hasImage ? "20px" : "30px 15px")};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const MenuName = styled.h3<{ $hasImage: boolean }>`
  margin: 0;
  font-size: ${(props) => (props.$hasImage ? "16px" : "18px")};
  color: #2d2d2d;
  text-align: center;
  line-height: 1.4;
`;

const MenuPrice = styled.p`
  margin: 0;
  color: #7b6a57;
  font-size: 15px;
  font-weight: 700;
`;

const BadgeBase = styled.div`
  position: absolute;
  left: 15px;
  color: white;
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 700;
  z-index: 2;
  @media (max-width: 600px) { font-size: 10px; padding: 4px 10px; left: 10px; }
`;

const HotBadge = styled(BadgeBase)`
  top: 15px;
  background: linear-gradient(135deg, #ff1c1c 0%, #ff4444 100%);
  @media (max-width: 600px) { top: 10px; }
`;

const IceBadge = styled(BadgeBase)<{ $isHotVisible?: boolean }>`
  top: ${({ $isHotVisible }) => $isHotVisible ? "47px" : "15px"};
  background: linear-gradient(135deg, #12bcff 0%, #44ccff 100%);
  @media (max-width: 600px) {
    top: ${({ $isHotVisible }) => $isHotVisible ? "35px" : "10px"};
  }
`;
